import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageSquare, Users, Clock, CheckCircle2, AlertTriangle, Loader2, Circle,
  Pause, Eye,
} from 'lucide-react';
import { api } from '../lib/api';

export default function ComplaintChatAdmin() {
  const [stats, setStats] = useState(null);
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      const [s, c] = await Promise.all([
        api.getComplaintChatStats(),
        api.getAllComplaintChats(filter ? { status: filter } : {}),
      ]);
      setStats(s.data); setChats(c.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const cards = useMemo(() => ([
    { label: 'Agents Online', value: stats?.agents_online || 0, icon: Users, color: 'from-emerald-500 to-emerald-700' },
    { label: 'Waiting', value: stats?.waiting_chats || 0, icon: Clock, color: 'from-amber-500 to-amber-700' },
    { label: 'Active Chats', value: stats?.active_chats || 0, icon: MessageSquare, color: 'from-cyan-500 to-cyan-700' },
    { label: 'Closed Today', value: stats?.closed_today || 0, icon: CheckCircle2, color: 'from-indigo-500 to-indigo-700' },
    { label: 'Fallback Today', value: stats?.fallback_today || 0, icon: AlertTriangle, color: 'from-rose-500 to-rose-700' },
  ]), [stats]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  return (
    <div data-testid="complaint-chat-admin" className="max-w-[1600px] mx-auto space-y-6 px-2 sm:px-0">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Live Complaint Chat</h1>
        <p className="text-sm text-slate-600 mt-1">Real-time queue, agent load and chat resolution.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className={`rounded-xl p-4 bg-gradient-to-br ${s.color} text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider opacity-80">{s.label}</div>
                  <div className="text-3xl font-bold mt-1">{s.value}</div>
                </div>
                <Icon size={28} className="opacity-40" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-1">
          <div className="font-semibold text-slate-800 mb-3">Agents</div>
          {(stats?.agents || []).length === 0 && <div className="text-sm text-slate-500">No agents registered yet</div>}
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {(stats?.agents || []).map((a) => (
              <div key={a.user_id} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`admin-agent-${a.user_id}`}>
                <div className="min-w-0 flex items-center gap-2">
                  <Circle size={10} className={a.online && !a.is_paused ? 'text-emerald-500 fill-emerald-500' : a.online ? 'text-amber-500 fill-amber-500' : 'text-slate-300 fill-slate-300'} />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800 truncate">{a.user_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {a.online ? (a.is_paused ? 'Paused' : 'Online') : 'Offline'}
                      {' · '}Load {a.active_count}/{a.max_concurrent}
                    </div>
                  </div>
                </div>
                {a.is_paused && a.online && <Pause size={12} className="text-amber-500" />}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 lg:col-span-2 overflow-hidden">
          <div className="p-3 border-b flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-slate-800">All Chats</div>
            <div className="flex gap-1 ml-auto">
              {[
                { v: '', l: 'All' },
                { v: 'waiting', l: 'Waiting' },
                { v: 'active', l: 'Active' },
                { v: 'closed', l: 'Closed' },
                { v: 'fallback', l: 'Fallback' },
              ].map((o) => (
                <button key={o.v} onClick={() => setFilter(o.v)}
                        data-testid={`admin-filter-${o.v || 'all'}`}
                        className={`px-3 py-1 rounded-md text-xs font-medium ${filter === o.v ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
            {chats.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No chats found</div>}
            {chats.map((c) => (
              <div key={c.id} className="p-3 hover:bg-slate-50" data-testid={`admin-chat-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.student_name} <span className="text-slate-400 font-normal">· {c.student_phone}</span></div>
                    <div className="text-xs text-slate-500 truncate">{c.problem_type} · {c.agent_name || 'Unassigned'} · {new Date(c.created_at).toLocaleString('en-IN')}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message_preview}</div>
                    {c.legacy_ticket_number && (
                      <div className="text-[11px] text-cyan-700 mt-1">Ticket: <code>{c.legacy_ticket_number}</code></div>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    c.status === 'active' ? 'bg-cyan-100 text-cyan-700'
                    : c.status === 'waiting' ? 'bg-amber-100 text-amber-700'
                    : c.status === 'closed' ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-rose-100 text-rose-700'
                  }`}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
